import type { UIMessageWithMetadata } from '../../../lib/types';

export interface ToolHandoffFinishState {
  isAbort: boolean;
  isDisconnect: boolean;
  isError: boolean;
  message: UIMessageWithMetadata;
}

export type ToolHandoffContinuationResult =
  | { outcome: 'succeeded'; finishState: ToolHandoffFinishState }
  | { outcome: 'failed'; finishState?: ToolHandoffFinishState };

interface PendingToolHandoffContinuation {
  resolve: (result: ToolHandoffContinuationResult) => void;
}

const pendingContinuations = new Map<string, PendingToolHandoffContinuation>();

export function beginToolHandoffContinuation(chatUuid: string): {
  outcome: Promise<ToolHandoffContinuationResult>;
  cancel: () => void;
} {
  if (pendingContinuations.has(chatUuid)) {
    throw new Error(`A tool handoff continuation is already active for chat ${chatUuid}.`);
  }

  let resolveOutcome: (result: ToolHandoffContinuationResult) => void = () => undefined;
  const outcome = new Promise<ToolHandoffContinuationResult>((resolve) => {
    resolveOutcome = resolve;
  });
  const pending = { resolve: resolveOutcome };
  pendingContinuations.set(chatUuid, pending);

  const settle = (result: ToolHandoffContinuationResult) => {
    if (pendingContinuations.get(chatUuid) !== pending) {
      return;
    }

    pendingContinuations.delete(chatUuid);
    pending.resolve(result);
  };

  return {
    outcome,
    cancel: () => settle({ outcome: 'failed' }),
  };
}

export function settleToolHandoffContinuation(chatUuid: string, finishState: ToolHandoffFinishState): void {
  const pending = pendingContinuations.get(chatUuid);
  if (!pending) {
    return;
  }

  pendingContinuations.delete(chatUuid);
  pending.resolve(
    finishState.isAbort || finishState.isDisconnect || finishState.isError
      ? { outcome: 'failed', finishState }
      : { outcome: 'succeeded', finishState },
  );
}
